<template>
  <DxPopup
    :visible.sync="getVisible"
    @hidden="popupHidden"
    :drag-enabled="false"
    :hide-on-outside-click="popupConfig.hideOnOutsideClick"
    :show-close-button="false"
    :show-title="true"
    :max-width="'95%'"
    :width="'85%'"
    :height="getModelHeight"
    :title="getTitle"
    :wrapper-attr="{
      class: 'css-v2 gc-popup gc-popup-form gc-popup-pallet-detail',
    }"
    ref="gc-popup"
    @shown="onShown"
    @initialized="onInitialized"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
      :template="item.template ? item.template : null"
    />

    <DxScrollView>
      <div class="details-section pallet-data-details">
        <dx-data-grid
          :columns="getColumns"
          :data-source="getDataSource"
          :twoWayBindingEnabled="false"
          :repaintChangesOnly="true"
          :ref="addPalletGridRef"
          :columnChooser="{
            enabled: false,
          }"
          :pager="{
            visible: false,
          }"
          :focusedRowEnabled="false"
          :key-expr="'ID'"
          :height="'auto'"
          @cell-prepared="onMainGridCellPrepared"
        >
          <DxSearchPanel :visible="false" />
          <DxPaging :page-size="200" :enabled="false" />

          <DxEditing
            :allow-updating="true"
            :allow-deleting="false"
            mode="cell"
            :new-row-position="'last'"
          />

          <DxKeyboardNavigation
            :edit-on-key-press="true"
            :enter-key-action="'startEdit'"
            :enter-key-direction="'none'"
            :enabled="true"
          />

          <!-- Master Detail Template -->
          <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
          <template #masterDetailTemplate="{ data: parentRow }">
            <!-- Check if it's a batch product -->
            <div v-if="parentRow.data.IsBatchProduct !== 'No'">
              <!-- Batch Product Grid -->
              <dx-data-grid
                :columns="getBatchColumns"
                :data-source="parentRow.data.BatchDataSource"
                :key-expr="'ID'"
                :twoWayBindingEnabled="false"
                :columnChooser="{ enabled: false }"
                :repaintChangesOnly="true"
                :show-borders="true"
                :width="'100%'"
                :ref="addBatchGridRef"
                @cell-click="onBatchCellClick"
                @cell-prepared="onBatchGridCellPrepared"
              >
                <DxPaging :page-size="200" :enabled="false" />
                <DxPager :showInfo="false" />
                <DxSearchPanel :visible="false" />

                <!-- Level 2 Master Detail for Batch Pallets -->
                <DxMasterDetail
                  :enabled="true"
                  template="batchPalletTemplate"
                />
                <template #batchPalletTemplate="{ data: batchRow }">
                  <dx-data-grid
                    :columns="getPalletColumns"
                    data-row-template="batchPalletRowTemplate"
                    :data-source="batchRow.data.PalletDataSource"
                    :key-expr="'ID'"
                    :twoWayBindingEnabled="false"
                    :columnChooser="{ enabled: false }"
                    :repaintChangesOnly="true"
                    :show-borders="true"
                    :width="'100%'"
                  >
                    <template #batchPalletRowTemplate="{ data: rowInfo }">
                      <div class="pallet-template">
                        <div>
                          <div class="pallet-data">
                            <div class="pallet">
                              <span class="text">Pallet No</span>
                              <DxNumberBox
                                :value="rowInfo.data.PalletID"
                                @value-changed="
                                  (e) =>
                                    onPalletFieldChange(
                                      rowInfo.data,
                                      'PalletID',
                                      e.value,
                                    )
                                "
                                :disabled="!allowEdit"
                              />
                            </div>

                            <div class="um">
                              <span class="text">Pick UM</span>
                              <span>{{ rowInfo.data.PickedUM }}</span>
                            </div>

                            <div class="picked-qty">
                              <span class="text">Pick Qty</span>
                              <DxNumberBox
                                :value="rowInfo.data.PickedQty"
                                @value-changed="
                                  (e) =>
                                    onPalletFieldChange(
                                      rowInfo.data,
                                      'PickedQty',
                                      e.value,
                                    )
                                "
                                :disabled="!allowEdit"
                                :format="'##,###,###,###,##0.0000'"
                              />
                            </div>
                          </div>

                          <!-- Delete Button -->
                          <div class="delete" v-if="allowEdit">
                            <DxButton
                              icon="trash"
                              type="danger"
                              styling-mode="text"
                              hint="Remove Pallet"
                              :on-click="
                                () =>
                                  onRemovePallet(batchRow.data, rowInfo.data)
                              "
                            />
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Column with custom header template -->
                    <DxColumn
                      data-field="PalletID"
                      width="100%"
                      caption=""
                      :allow-sorting="false"
                      header-cell-template="batchPalletHeaderTemplate"
                    />

                    <template #batchPalletHeaderTemplate>
                      <div class="pallet-grid-header">
                        <DxButton
                          v-if="allowEdit"
                          text="Add Pallet"
                          icon="add"
                          type="default"
                          styling-mode="outlined"
                          :on-click="
                            () => onAddPallet(parentRow.data, batchRow.data)
                          "
                        />
                      </div>
                    </template>

                    <DxPaging :page-size="200" :enabled="false" />
                    <DxPager :showInfo="false" />
                    <DxSearchPanel :visible="false" />

                    <DxEditing
                      :allow-updating="true"
                      :allow-deleting="false"
                      mode="cell"
                    />
                  </dx-data-grid>
                </template>
              </dx-data-grid>
            </div>

            <!-- Non-Batch Product Grid (Direct Pallets) -->
            <div v-else>
              <dx-data-grid
                :columns="getPalletColumns"
                data-row-template="itemPalletRowTemplate"
                :data-source="parentRow.data.PalletDataSource"
                :key-expr="'ID'"
                :twoWayBindingEnabled="false"
                :columnChooser="{ enabled: false }"
                :repaintChangesOnly="true"
                :show-borders="true"
                :width="'100%'"
                @cell-prepared="onPalletCellPrepared"
                @editor-preparing="onPalletEditorPreparing"
              >
                <template #itemPalletRowTemplate="{ data: rowInfo }">
                  <div class="pallet-template">
                    <div>
                      <div class="pallet-data">
                        <div class="pallet">
                          <span class="text">Pallet No</span>
                          <DxNumberBox
                            :value="rowInfo.data.PalletID"
                            @value-changed="
                              (e) =>
                                onPalletFieldChange(
                                  rowInfo.data,
                                  'PalletID',
                                  e.value,
                                )
                            "
                            :disabled="!allowEdit"
                          />
                        </div>

                        <div class="um">
                          <span class="text">Pick UM</span>
                          <span>{{ rowInfo.data.PickedUM }}</span>
                        </div>

                        <div class="picked-qty">
                          <span class="text">Pick Qty</span>
                          <DxNumberBox
                            :value="rowInfo.data.PickedQty"
                            @value-changed="
                              (e) =>
                                onPalletFieldChange(
                                  rowInfo.data,
                                  'PickedQty',
                                  e.value,
                                )
                            "
                            :disabled="!allowEdit"
                            :format="'##,###,###,###,##0.0000'"
                          />
                        </div>
                      </div>

                      <!-- Delete Button -->
                      <div class="delete" v-if="allowEdit">
                        <DxButton
                          icon="trash"
                          type="danger"
                          styling-mode="text"
                          hint="Remove Pallet"
                          :on-click="
                            () => onRemovePallet(parentRow.data, rowInfo.data)
                          "
                        />
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Column with custom header template -->
                <DxColumn
                  data-field="PalletID"
                  width="100%"
                  caption=""
                  :allow-sorting="false"
                  header-cell-template="itemPalletHeaderTemplate"
                />

                <template #itemPalletHeaderTemplate>
                  <div class="pallet-grid-header">
                    <DxButton
                      v-if="allowEdit"
                      text="Add Pallet"
                      icon="add"
                      type="default"
                      styling-mode="outlined"
                      :on-click="() => onAddPallet(parentRow.data, null)"
                    />
                  </div>
                </template>

                <DxPaging :page-size="200" :enabled="false" />
                <DxPager :showInfo="false" />
                <DxSearchPanel :visible="false" />

                <DxEditing
                  :allow-updating="popupConfig.formMode == 'edit'"
                  :allow-deleting="false"
                  mode="cell"
                />
              </dx-data-grid>
            </div>
          </template>
        </dx-data-grid>
      </div>
    </DxScrollView>
  </DxPopup>
</template>

<script>
import DataGridMixin from "@/_mixins/data-grid.mixin";
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../_helpers/data-grid/ColumnWidth";
import { mapGetters } from "vuex";
import { DeliveryNoteService } from "../_services/api/sales/delivery-note.service";
import { sortByPalletID } from "../_helpers/collections/array-helper";

export default {
  mixins: [DataGridMixin],
  components: {},
  props: {
    setVisible: {
      type: Boolean,
      default: false,
    },
    hidden: {
      type: Function,
      default: () => {},
    },
    showLoading: {
      type: Function,
      default: function () {},
    },
    dataObj: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      dataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      hasUnsavedChanges: false,
      deliveryNote: null,
      salesOrderNo: null,
      invoiceNo: null,
      customerName: null,
      popupConfig: {
        isPopupShow: false,
        title: "",
        formMode: "review", // review, edit
        hideOnOutsideClick: false,
      },
      allowEdit: false,
      palletIdCounter: 0,
      batchGridRefs: [],
      palletGridRefs: [],
    };
  },
  async created() {
    // Initialize from dataObj passed from parent
    if (this.dataObj) {
      this.deliveryNote = this.dataObj.DeliveryNote;
      this.salesOrderNo = this.dataObj.SalesOrderNo;
      this.invoiceNo = this.dataObj.InvoiceNo;
      this.customerName = this.dataObj.CustomerName;
    }
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),

    apiService() {
      return {
        getDeliveryNotePalletDetailComplete: (deliveryNote) => {
          return DeliveryNoteService.getDeliveryNotePalletDetailComplete(
            deliveryNote,
          );
        },
        updatePalletDetails: (data) => {
          return DeliveryNoteService.updatePalletDetails(data);
        },
      };
    },

    getTitle() {
      return `Delivery #${this.deliveryNote} for Sales Order #${this.salesOrderNo} #Invoice ${this.invoiceNo} - ${this.customerName}`;
    },

    getVisible: {
      get() {
        return this.setVisible;
      },
      set() {
        return false;
      },
    },

    getModelHeight() {
      return "80vh";
    },

    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: this.popupConfig.formMode === "review",
          options: {
            icon: "edit",
            onClick: this.onEditPopupHandler,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: this.popupConfig.formMode === "edit",
          options: {
            text: "Save",
            stylingMode: "contained",
            type: "default",
            onClick: this.onSavePopupHandler,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            icon: "close",
            onClick: this.onCancelPopupHandler,
          },
        },
      ];
    },

    getColumns() {
      return [
        {
          caption: "",
          dataField: "",
          type: "detailExpand",
          fixed: true,
          fixedPosition: "left",
          visibleIndex: 0,
          width: 30,
          minWidth: 30,
        },
        {
          caption: "Item Code",
          dataField: "ProductCode",
          allowEditing: false,
          width: ColumnWidth.ProductCode.width,
          minWidth: ColumnWidth.ProductCode.minWidth,
        },
        {
          caption: "Description",
          dataField: "ProductDescription",
          allowEditing: false,
          minWidth: 300,
        },
        {
          caption: "Picked UM",
          dataField: "PickedUM",
          allowEditing: false,
          width: 100,
        },
        {
          caption: "Picked Quantity",
          dataField: "Delivered",
          allowEditing: false,
          dataType: "number",
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
        },
      ];
    },

    getBatchColumns() {
      return [
        {
          caption: "",
          dataField: "",
          type: "detailExpand",
          fixed: true,
          fixedPosition: "left",
          visibleIndex: 0,
          width: 30,
          minWidth: 30,
        },
        {
          caption: "Batch No",
          dataField: "BatchNo",
          allowEditing: false,
          width: 150,
          minWidth: 150,
        },
        {
          caption: "Manufactured Date",
          dataField: "ManufacturedDate",
          allowEditing: false,
          dataType: "date",
          format: "dd MMM yyyy",
          width: 150,
          minWidth: 150,
        },
        {
          caption: "Batch Quantity",
          dataField: "BatchQty",
          allowEditing: false,
          dataType: "number",
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
        },
      ];
    },

    getPalletColumns() {
      return [
        {
          caption: "Pallet ID",
          dataField: "PalletID",
          allowEditing: true,
          sortIndex: 1,
          sortOrder: "asc",
          cssClass:
            this.popupConfig.formMode === "edit"
              ? "gc-editable-cell-color"
              : undefined,
          width: 150,
          minWidth: 150,
        },
        {
          caption: "Picked UM",
          dataField: "PickedUM",
          allowEditing: false,
          width: 100,
          minWidth: 100,
        },
        {
          caption: "Pick Quantity",
          dataField: "PickedQty",
          allowEditing: true,
          cssClass:
            this.popupConfig.formMode === "edit"
              ? "gc-editable-cell-color"
              : undefined,
          dataType: "number",
          format: "##,###,###,###,##0.0000",
          width: ColumnWidth.Quantity2DecimalPoints.width,
          minWidth: ColumnWidth.Quantity2DecimalPoints.minWidth,
          editorOptions: {
            format: "##,###,###,###,##0.0000",
          },
        },
      ];
    },

    getDataSource() {
      return this.dataSource._array;
    },
  },
  methods: {
    popupHidden() {
      if (this.hidden) this.hidden();
    },

    addPalletGridRef(el) {
      if (el && !this.palletGridRefs.includes(el)) {
        this.palletGridRefs.push(el);
      }
    },

    refreshRowsWithNoBatch() {
      this.$nextTick(() => {
        this.palletGridRefs.forEach((gridRef) => {
          if (gridRef && gridRef.instance) {
            const grid = gridRef.instance;
            const rowIndexes = [];

            // Find all rows without batch
            grid.getVisibleRows().forEach((row) => {
              if (
                row.rowType === "data" &&
                (!row.data.BatchID || row.data.BatchID === null)
              ) {
                rowIndexes.push(row.rowIndex);
              }
            });

            // Refresh only those specific rows
            if (rowIndexes.length > 0) {
              grid.repaintRows(rowIndexes);
            }
          }
        });
      });
    },

    addBatchGridRef(el) {
      if (el && !this.batchGridRefs.includes(el)) {
        this.batchGridRefs.push(el);
      }
    },

    onMainGridCellPrepared(e) {
      // Check if this is the expand/collapse column
      if (e.rowType === "data" && e.column.command === "expand") {
        //
        if (this.popupConfig.formMode === "edit") {
          return;
        }
        //
        const rowData = e.data;
        let hasDetailData = false;

        // Check if it's a batch product with batch data
        if (rowData.IsBatchProduct !== "No") {
          hasDetailData =
            rowData.BatchDataSource && rowData.BatchDataSource.length > 0;
        } else {
          // Check if it's a non-batch product with pallet data
          hasDetailData =
            rowData.PalletDataSource && rowData.PalletDataSource.length > 0;
        }

        // Hide the expand icon if there's no detail data
        if (!hasDetailData) {
          e.cellElement.style.visibility = "hidden";
          e.cellElement.style.pointerEvents = "none";
        }
      }
    },

    onBatchGridCellPrepared(e) {
      // Check if this is the expand/collapse column
      if (e.rowType === "data" && e.column.command === "expand") {
        //
        if (this.popupConfig.formMode === "edit") {
          return;
        }
        //
        const rowData = e.data;
        let hasDetailData =
          rowData.PalletDataSource && rowData.PalletDataSource.length > 0;

        // Hide the expand icon if there's no detail data
        if (!hasDetailData) {
          e.cellElement.style.visibility = "hidden";
          e.cellElement.style.pointerEvents = "none";
        }
      }
    },

    async onShown() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", true);
      this.hasUnsavedChanges = false;
      this.showLoading(true);

      // Clear existing data
      this.dataSource = new ArrayStore({
        key: "ID",
        data: [],
      });

      await this.loadParentData();

      this.showLoading(false);
    },

    async loadParentData() {
      if (!this.deliveryNote || !this.apiService) {
        // console.error("DeliveryNote or API Service not provided");
        return;
      }

      await this.loadParentDataOptimized();
    },

    /**
     * Load all data in ONE API call using view
     */
    async loadParentDataOptimized() {
      try {
        const response =
          await this.apiService.getDeliveryNotePalletDetailComplete(
            this.deliveryNote,
          );

        if (response.data && response.data.value) {
          const flatData = response.data.value;

          // Group data hierarchically: Product -> Batch -> Pallet
          const productsMap = new Map();

          flatData.forEach((row) => {
            const productKey = row.SalesOrderLine + "_" + row.ProductCode;

            // Get or create product
            if (!productsMap.has(productKey)) {
              productsMap.set(productKey, {
                //
                PickId: row.PickId,
                SalesOrderNo: row.SalesOrderNo,
                SalesOrderLine: row.SalesOrderLine,
                DeliveryNoteNo: row.DeliveryNoteNo,
                DeliveryNoteLine: row.DeliveryNoteLine,
                CustomerInvoiceNo: row.CustomerInvoiceNo,
                CustomerInvoiceLine: row.CustomerInvoiceLine,
                ItemCode: row.ItemCode,
                ProductCode: row.ProductCode,
                SalesUM: row.SalesUM,
                SalesUMInvoiceConversion: row.SalesUMInvoiceConversion,
                ProductDescription: row.ProductDescription,
                Delivered: row.TotalPickedQty,
                InvoiceNo: row.InvoiceNo,
                InvoiceLineNo: row.InvoiceLineNo,
                IsBatchProduct: row.IsBatchProduct,
                ItemType: row.ItemType,
                PickedUM: row.PickedUM,
                PickedUMConversion: row.PickedUMConversion,
                StockUMConversion: row.StockUMConversion,
                SalesUMConversion: row.SalesUMConversion,
                BatchDataSource: [],
                PalletDataSource: [],
                batchMap: new Map(),
              });
            }

            const product = productsMap.get(productKey);

            // Continue with normal hierarchy
            if (row.IsBatchProduct !== "No" && row.BatchNo) {
              // Batch product
              const batchKey = row.BatchNo;

              // Get or create batch
              if (!product.batchMap.has(batchKey)) {
                const batch = {
                  ID: `${row.ProductCode}_${row.BatchNo}`,
                  BatchNo: row.BatchNo,
                  ManufacturedDate: row.ManufacturedDate,
                  SupplierCode: row.SupplierCode,
                  BatchQty: row.BatchQty,
                  PickedUMConversion: row.PickedUMConversion,
                  StockUMConversion: row.StockUMConversion,
                  SalesUMConversion: row.SalesUMConversion,
                  PalletDataSource: [],
                };
                product.batchMap.set(batchKey, batch);
                product.BatchDataSource.push(batch);
              }

              const batch = product.batchMap.get(batchKey);

              // Add pallet to batch
              if (row.PalletId) {
                batch.PalletDataSource.push({
                  PalletID: row.PalletId,
                  PickedUM: row.PickedUM,
                  PickedQty: row.PickedQty,
                  ID: `${row.ProductCode}_BATCH_${row.BatchNo}_${batch.PalletDataSource.length}`,
                  OriginalPalletID: row.PalletId,
                  OriginalPickedQty: row.PickedQty,
                  PickingSlipLogRecordId: row.PickingSlipLogRecordId,
                  PickedUMConversion: row.PickedUMConversion,
                  StockUMConversion: row.StockUMConversion,
                  SalesUMConversion: row.SalesUMConversion,
                  IsNew: false,
                });
              }
            } else {
              // Non-batch stock product - direct pallet
              if (row.PalletId) {
                product.PalletDataSource.push({
                  PalletID: row.PalletId,
                  PickedUM: row.PickedUM,
                  PickedQty: row.PickedQty,
                  ID: `${row.ProductCode}_NOBATCH_${product.PalletDataSource.length}`,
                  OriginalPalletID: row.PalletId,
                  OriginalPickedQty: row.PickedQty,
                  PickingSlipLogRecordId: row.PickingSlipLogRecordId,
                  PickedUMConversion: row.PickedUMConversion,
                  StockUMConversion: row.StockUMConversion,
                  SalesUMConversion: row.SalesUMConversion,
                  IsNew: false,
                });
              }
            }
          });

          // Convert map to array and insert into grid
          productsMap.forEach((product) => {
            // Sort PalletDataSource by PalletID ascending
            if (Array.isArray(product.PalletDataSource)) {
              sortByPalletID(product.PalletDataSource);
            }
            // Sort each batch's PalletDataSource by PalletID ascending
            if (Array.isArray(product.BatchDataSource)) {
              product.BatchDataSource.forEach((batch) => {
                if (Array.isArray(batch.PalletDataSource)) {
                  sortByPalletID(batch.PalletDataSource);
                }
              });
            }
            delete product.batchMap; // Remove temporary map
            this.dataSource.insert(product);
          });
        }
      } catch (error) {
        // console.error("Error loading optimized data:", error);
        throw error;
      }
    },

    onInitialized(e) {
      if (this.popupConfig.formMode !== "review")
        e.component.registerKeyHandler("escape", (arg) => {
          arg.stopPropagation();
          this.onCancelPopupHandler();
        });
    },

    /**
     * Add new pallet to either line level or batch level
     * @param {Object} parentItem - The parent item (line level)
     * @param {Object} batchData - The batch data (null if line level)
     */
    onAddPallet(parentItem, batchData) {
      this.palletIdCounter++;

      // Get the first existing pallet to copy UM and conversion values
      let templatePallet = null;
      if (batchData && batchData.PalletDataSource.length > 0) {
        templatePallet = batchData.PalletDataSource[0];
      } else if (parentItem.PalletDataSource.length > 0) {
        templatePallet = parentItem.PalletDataSource[0];
      } else {
        templatePallet = parentItem;
      }

      const newPallet = {
        ID: `new-pallet-${this.palletIdCounter}`,
        PalletID: null,
        PickedUM: templatePallet ? templatePallet.PickedUM : "",
        PickedQty: 0,
        OriginalPalletID: null,
        OriginalPickedQty: 0,
        PickingSlipLogRecordId: null,
        PickedUMConversion: templatePallet.PickedUMConversion,
        StockUMConversion: templatePallet.StockUMConversion,
        SalesUMConversion: templatePallet.SalesUMConversion,
        IsNew: true,
      };

      if (batchData) {
        // Add to batch level
        batchData.PalletDataSource.push(newPallet);
      } else {
        // Add to line level
        parentItem.PalletDataSource.push(newPallet);
      }

      this.hasUnsavedChanges = true;
      this.$forceUpdate();
    },

    // NEW METHOD: Remove pallet
    /**
     * Remove pallet from either line level or batch level
     * @param {Object} parentData - The parent data (either line item or batch)
     * @param {Object} palletData - The pallet to remove
     */
    async onRemovePallet(parentData, palletData) {
      const confirm = await this.$confirm(
        `Are you sure you want to remove this pallet?`,
        "Remove Pallet",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          type: "warning",
        },
      );

      if (!confirm) return;

      const palletDataSource = parentData.PalletDataSource;
      const index = palletDataSource.findIndex((p) => p.ID === palletData.ID);

      if (index > -1) {
        if (palletData.IsNew) {
          // New pallet: just remove it
          palletDataSource.splice(index, 1);
        } else {
          // Existing pallet: mark as deleted AND remove from array
          palletData.IsDeleted = true;

          // Store it in a separate deletedPallets array for payload
          if (!parentData.DeletedPallets) {
            parentData.DeletedPallets = [];
          }
          parentData.DeletedPallets.push(palletData);

          // Remove from visible array
          palletDataSource.splice(index, 1);
        }

        this.hasUnsavedChanges = true;
        this.$forceUpdate();
      }
    },

    /**
     * Handle pallet field changes
     * @param {Object} palletData - The pallet data
     * @param {String} field - Field name
     * @param {*} value - New value
     */
    onPalletFieldChange(palletData, field, value) {
      palletData[field] = value;
      this.hasUnsavedChanges = true;
    },

    async onSavePopupHandler() {
      // Validate that all pallets have Pallet ID
      const missingPalletIds = this.validatePalletIds();
      if (missingPalletIds.length > 0) {
        const errorMessage = missingPalletIds.join("<br/>");
        this.$alert(errorMessage, "Validation Errors");
        return;
      }

      // Validate pallet quantities before collecting payload
      const validationErrors = this.validatePalletQuantities();
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.join("<br/>");
        this.$alert(errorMessage, "Validation Errors");
        return;
      }

      let payload = this.getPayload();

      if (payload.PalletInfoList.length === 0) {
        this.$notify("No changes to save", "info");
        return;
      }

      this.showLoading(true);

      try {
        await this.apiService.updatePalletDetails(payload);
        this.$notify("Pallet details updated successfully", "success");
        this.hasUnsavedChanges = false;
        this.popupHidden();
      } catch (error) {
        this.$notify("Error saving pallet details", "error");
      } finally {
        this.showLoading(false);
      }
    },

    getPayload() {
      const palletInfoList = [];

      // Helper function to build pallet info object
      const buildPalletInfo = (
        parentItem,
        batch,
        pallet,
        isDeleted = false,
      ) => {
        return {
          PickId: parentItem.PickId,
          PickingSlipLogRecordId: pallet.IsNew
            ? "00000000-0000-0000-0000-000000000000"
            : pallet.PickingSlipLogRecordId,
          SalesOrderNo: parentItem.SalesOrderNo,
          SalesOrderLine: parentItem.SalesOrderLine,
          DeliveryNoteNo: parentItem.DeliveryNoteNo,
          DeliveryNoteLine: parentItem.DeliveryNoteLine,
          CustomerInvoiceNo: parentItem.CustomerInvoiceNo,
          CustomerInvoiceLine: parentItem.CustomerInvoiceLine,
          ItemCode: parentItem.ProductCode,
          BatchNo: batch ? batch.BatchNo : null,
          SupplierCode: batch ? batch.SupplierCode : null,
          PalletId: pallet.PalletID,
          StockUMQty:
            pallet.PickedQty *
            (pallet.PickedUMConversion / pallet.StockUMConversion),
          SalesUMQty:
            pallet.PickedQty *
            (pallet.PickedUMConversion / pallet.SalesUMConversion),
          SalesUM: parentItem.SalesUM,
          SalesUMInvoiceConversion: parentItem.SalesUMInvoiceConversion,
          IsDeleted: isDeleted ? "Y" : "N",
        };
      };

      // Helper function to process pallet array
      const processPallets = (parentItem, batch, pallets) => {
        pallets.forEach((pallet) => {
          if (
            pallet.IsNew ||
            pallet.PalletID !== pallet.OriginalPalletID ||
            pallet.PickedQty !== pallet.OriginalPickedQty
          ) {
            palletInfoList.push(
              buildPalletInfo(parentItem, batch, pallet, false),
            );
          }
        });
      };

      // Helper function to process deleted pallets
      const processDeletedPallets = (parentItem, batch, deletedPallets) => {
        if (deletedPallets && deletedPallets.length > 0) {
          deletedPallets.forEach((pallet) => {
            palletInfoList.push(
              buildPalletInfo(parentItem, batch, pallet, true),
            );
          });
        }
      };

      // Iterate through dataSource and collect modified pallet records
      this.dataSource._array.forEach((parentItem) => {
        if (parentItem.IsBatchProduct !== "No") {
          // Batch products
          parentItem.BatchDataSource.forEach((batch) => {
            processPallets(parentItem, batch, batch.PalletDataSource);
            processDeletedPallets(parentItem, batch, batch.DeletedPallets);
          });
        } else {
          // Non-batch products
          processPallets(parentItem, null, parentItem.PalletDataSource);
          processDeletedPallets(parentItem, null, parentItem.DeletedPallets);
        }
      });

      let payload = {
        DeliveryNote: this.deliveryNote,
        PalletInfoList: palletInfoList,
      };

      return payload;
    },

    validatePalletIds() {
      const errors = [];

      // Iterate through all items in the data source
      this.dataSource._array.forEach((parentItem) => {
        const itemDescription = parentItem.ProductDescription;

        // Check if it's a batch product
        if (parentItem.IsBatchProduct !== "No") {
          // Batch product - validate each batch's pallets
          parentItem.BatchDataSource.forEach((batch) => {
            const batchNo = batch.BatchNo;

            batch.PalletDataSource.forEach((pallet, index) => {
              if (
                !pallet.PalletID ||
                pallet.PalletID === null ||
                pallet.PalletID === ""
              ) {
                errors.push(
                  `${itemDescription}\n` +
                    `Batch: ${batchNo}\n` +
                    `Pallet #${index + 1}: Pallet ID is required`,
                );
              }
            });
          });
        } else {
          // Non-batch product - validate pallets directly
          parentItem.PalletDataSource.forEach((pallet, index) => {
            if (
              !pallet.PalletID ||
              pallet.PalletID === null ||
              pallet.PalletID === ""
            ) {
              errors.push(
                `${itemDescription}\n` +
                  `Pallet #${index + 1}: Pallet ID is required`,
              );
            }
          });
        }
      });

      return errors;
    },

    validatePalletQuantities() {
      const errors = [];

      // Iterate through all items in the data source
      this.dataSource._array.forEach((parentItem) => {
        // const itemCode = parentItem.ProductCode;
        const itemDescription = parentItem.ProductDescription;

        // Check if it's a batch product
        if (parentItem.IsBatchProduct !== "No") {
          // Batch product - validate each batch's pallets against batch quantity
          parentItem.BatchDataSource.forEach((batch) => {
            const batchNo = batch.BatchNo;
            const batchQty = batch.BatchQty || 0;

            // Calculate total picked quantity for all pallets in this batch
            let totalPalletQty = 0;
            batch.PalletDataSource.forEach((pallet) => {
              totalPalletQty += parseFloat(pallet.PickedQty || 0);
            });

            // Validate: total pallet quantity should not exceed batch quantity
            if (totalPalletQty != batchQty) {
              errors.push(
                `${itemDescription}\n` +
                  `Batch: ${batchNo}\n` +
                  `total pallet picked quantity (${totalPalletQty.toFixed(
                    4,
                  )}) should be equal to (${batchQty.toFixed(4)})`,
              );
            }
          });
        } else {
          // Non-batch product - validate pallets directly against item quantity
          const itemPickedQty = parentItem.Delivered || 0;

          // Calculate total picked quantity for all pallets
          let totalPalletQty = 0;
          parentItem.PalletDataSource.forEach((pallet) => {
            totalPalletQty += parseFloat(pallet.PickedQty || 0);
          });

          // Validate: total pallet quantity should not exceed item picked quantity
          if (totalPalletQty != itemPickedQty) {
            errors.push(
              `${itemDescription}\n` +
                `total pallet picked quantity (${totalPalletQty.toFixed(
                  4,
                )}) should be equal to (${itemPickedQty.toFixed(4)})`,
            );
          }
        }
      });

      return errors;
    },

    async onCancelPopupHandler() {
      if (this.popupConfig.formMode === "review") {
        this.popupHidden();
        return;
      }

      if (this.hasUnsavedChanges) {
        const confirm = await this.$confirm(
          `You have unsaved changes. Are you sure you want to leave?`,
          "Unsaved changes",
          {
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            type: "warning",
          },
        );
        if (!confirm) return;
      }

      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.popupHidden();
    },

    onEditPopupHandler() {
      this.popupConfig.formMode = "edit";
      this.allowEdit = true;
      this.$forceUpdate();

      this.$nextTick(() => {
        this.batchGridRefs.forEach((gridRef) => {
          if (gridRef && gridRef.instance) {
            gridRef.instance.refresh();
          }
        });
      });

      this.refreshRowsWithNoBatch();
    },

    onBatchCellClick(e) {
      // Only handle clicks on the expand column
      if (e.column && e.column.command === "expand" && e.data.ID) {
        const isExpanded = e.component.isRowExpanded(e.data.ID);
        if (isExpanded) {
          e.component.collapseRow(e.data.ID);
        } else {
          e.component.expandRow(e.data.ID);
        }
      }
    },

    onPalletCellPrepared(e) {
      if (e.rowType == "data") {
        if (this.popupConfig.formMode == "review") {
          e.cellElement.classList.remove("gc-editable-cell-color");
        } else if (e.column.allowEditing) {
          e.cellElement.classList.add("gc-editable-cell-color");
        }
      }
    },

    onPalletEditorPreparing(e) {
      if (e.parentType == "dataRow") {
        const editableFields = ["PalletID", "PickedQty"];

        if (editableFields.includes(e.dataField)) {
          // Track changes when editor value changes
          const originalValueChanged = e.editorOptions.onValueChanged;
          e.editorOptions.onValueChanged = (args) => {
            // Update the data immediately
            if (e.row && e.row.data) {
              e.row.data[e.dataField] = args.value;
            }

            this.hasUnsavedChanges = true;
            if (originalValueChanged) originalValueChanged(args);
          };
        }
      }
    },
  },
  watch: {
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          if (this.popupConfig.formMode !== "review") {
            this.onSavePopupHandler();
          }
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-pallet-detail {
  .details-section.pallet-data-details {
    padding: 12px;

    .gc-editable-cell-color[role="gridcell"]:not(.cell-disabled) {
      background-color: #f8fdfe !important;
    }

    .dx-datagrid
      .dx-datagrid-content
      .dx-datagrid-table
      .dx-row
      .dx-editor-cell {
      padding-left: 5px !important;
      padding-right: 5px !important;
    }

    // Nested grids styling
    .dx-datagrid-rowsview .dx-master-detail-cell {
      padding: 8px;
      background-color: #f9f9f9;
    }

    // Level 2 nested grid (batch pallets)
    .dx-datagrid .dx-datagrid .dx-master-detail-cell {
      background-color: #f3f3f3;
      padding: 6px;
    }

    // Pointer cursor for batch expand icon
    .dx-datagrid .dx-datagrid .dx-command-expand {
      cursor: pointer;
    }
  }

  .picked-qty {
    display: flex;
    flex-direction: column;
    align-items: flex-end; // Right align content
    max-width: 80px;
  }

  .dx-toolbar.dx-popup-title {
    min-height: 50px;
  }

  // Pallet Grid Header with Add Button
  .pallet-grid-header {
    display: flex !important;
    justify-content: flex-end !important;
    align-items: center;
    background-color: #f9f9f9;
    height: 30px;
    border-bottom: 1px solid var(--Colors-Border-border-primary, #d0d5dd);
    width: 100%;
  }

  // Override DevExtreme's default header cell alignment ONLY for pallet grids
  .dx-master-detail-cell {
    .dx-datagrid-headers .dx-datagrid-table .dx-row > td {
      &:has(.pallet-grid-header) {
        text-align: right !important;
      }
    }
  }
}

.pallet-template {
  border: 1px solid var(--Colors-Border-border-primary, #d0d5dd);
  padding: 10px;
  background: var(--Colors-Foreground-fg-white, #ffffff);
  gap: 6px;

  > div {
    display: flex;
    justify-content: space-between;

    .pallet-data {
      display: flex;
      justify-content: space-between;
      flex: 1;

      .um {
        text-align: center;
        justify-content: space-between;

        > span:last-child {
          font-family: Inter;
          font-weight: 600;
          font-size: 12px;
          leading-trim: NONE;
          line-height: 18px;
          letter-spacing: 0%;
          color: var(--colors-text-text-secondary-700, #323842);
        }
      }

      span.text {
        font-family: Roboto;
        font-weight: 400;
        font-size: 12px;
        leading-trim: NONE;
        line-height: 18px;
        letter-spacing: 0%;
        color: var(--Colors-Text-text-placeholder, #667085);
      }

      > div {
        display: flex;
        flex-direction: column;
        max-width: 80px;
      }
    }

    // NEW: Delete button styling
    .delete {
      display: flex;
      align-items: center;
      margin-left: 12px;

      .dx-button {
        &.dx-button-danger {
          .dx-icon {
            color: #f04438;
          }

          &:hover {
            background-color: #fef3f2;
          }
        }
      }
    }
  }
}
</style>
